import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { eur } from "@/lib/format";
import { catLottoLabel, catBeneLabel, mapsUrl, DISP, ESITO } from "@/pages/FinderAste";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ExternalLink, MapPin, Star, Clock, Ruler, Users, FileText,
  Phone, Mail, Lock, TrendingDown, Image as ImageIcon, Gavel, Share2, MessageCircle,
  Send, Facebook, Twitter, Link as LinkIcon, Download, X, ChevronLeft, ChevronRight,
} from "lucide-react";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const RUOLO_LABELS = {
  GIUDICE: "Giudice",
  DELEGATO: "Delegato alla vendita",
  CURATOR: "Curatore",
  CUSTODE: "Custode giudiziario",
  LIQUGIU: "Liquidatore giudiziale",
  IVG: "Istituto Vendite Giudiziarie",
};

const DOC_TYPE_LABELS = {
  PERIZ: "Perizia",
  AVEND: "Avviso di vendita",
  ORDIN: "Ordinanza / Sentenza",
  PROVV: "Provvedimento",
  ALTRO: "Foto / Altro",
};

const STATO_OPTIONS = [
  { value: "da_valutare", label: "Da valutare", color: "#3B82F6" },
  { value: "interessante", label: "Interessante", color: "#10B981" },
  { value: "scartata", label: "Scartata", color: "#71717A" },
  { value: "offerta_presentata", label: "Offerta presentata", color: "#F59E0B" },
  { value: "aggiudicata", label: "Aggiudicata", color: "#22C55E" },
];
const statoInfo = (v) => STATO_OPTIONS.find((s) => s.value === v);

const formatBytes = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

function Section({ title, icon: Icon, children, action, testid, locked }) {
  return (
    <div className={`bg-[#122A3F] border border-white/10 rounded-sm ${locked ? "opacity-60" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <h2 className="flex items-center gap-2 text-sm font-medium text-zinc-200 uppercase tracking-wider">
          {Icon && <Icon className="w-4 h-4 text-[#EA7B2B]" />} {title}
        </h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  if (!targetIso) return null;
  return new Date(targetIso).getTime() - now;
}

function Countdown({ scadenza }) {
  const diff = useCountdown(scadenza);
  if (diff == null) return <div className="text-sm text-zinc-500">Termine per la presentazione delle offerte non disponibile.</div>;
  const scaduto = diff <= 0;
  const abs = Math.abs(diff);
  const gg = Math.floor(abs / 86400000);
  const hh = Math.floor((abs % 86400000) / 3600000);
  const mm = Math.floor((abs % 3600000) / 60000);
  const ss = Math.floor((abs % 60000) / 1000);
  const data = new Date(scadenza).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div>
      <div className={`flex items-baseline gap-3 font-mono ${scaduto ? "text-rose-400" : "text-white"}`} data-testid="asta-countdown">
        <span className="text-3xl">{gg}<span className="text-sm text-zinc-500">g</span></span>
        <span className="text-3xl">{String(hh).padStart(2, "0")}<span className="text-sm text-zinc-500">h</span></span>
        <span className="text-3xl">{String(mm).padStart(2, "0")}<span className="text-sm text-zinc-500">m</span></span>
        <span className="text-3xl">{String(ss).padStart(2, "0")}<span className="text-sm text-zinc-500">s</span></span>
      </div>
      <div className="text-xs text-zinc-500 mt-2">
        {scaduto ? "Termine per presentare offerte scaduto il " : "Termine per presentare offerte: "}
        <span className="text-zinc-300">{data}</span>
      </div>
    </div>
  );
}

export default function AstaDettaglio() {
  const { idAnnuncio } = useParams();
  const navigate = useNavigate();
  const [asta, setAsta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notaText, setNotaText] = useState("");
  const [savingNota, setSavingNota] = useState(false);
  const [togglingPreferito, setTogglingPreferito] = useState(false);
  const [savingStato, setSavingStato] = useState(false);
  const [mapMode, setMapMode] = useState("streetview");
  const [shareOpen, setShareOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/aste/${idAnnuncio}/dettaglio`)
      .then((r) => { setAsta(r.data); setNotaText(r.data.nota || ""); })
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); else toast.error("Errore nel caricamento dell'annuncio"); })
      .finally(() => setLoading(false));
  }, [idAnnuncio]);

  useEffect(() => { load(); }, [load]);

  const togglePreferito = async () => {
    setTogglingPreferito(true);
    try {
      const r = await api.put(`/aste/${idAnnuncio}/preferito`, { preferito: !asta.preferito });
      setAsta((a) => ({ ...a, preferito: r.data.preferito }));
    } catch {
      toast.error("Errore nell'aggiornamento dei preferiti");
    } finally {
      setTogglingPreferito(false);
    }
  };

  const changeStato = async (stato) => {
    setSavingStato(true);
    try {
      const r = await api.put(`/aste/${idAnnuncio}/stato`, { stato: stato || null });
      setAsta((a) => ({ ...a, stato: r.data.stato }));
    } catch {
      toast.error("Errore nell'aggiornamento della classificazione");
    } finally {
      setSavingStato(false);
    }
  };

  const saveNota = async () => {
    setSavingNota(true);
    try {
      await api.put(`/aste/${idAnnuncio}/nota`, { nota: notaText });
      toast.success("Nota salvata");
    } catch {
      toast.error("Errore nel salvataggio della nota");
    } finally {
      setSavingNota(false);
    }
  };

  useEffect(() => {
    if (lightboxIndex == null) return;
    const fotoList = (asta?.foto || []).filter((f) => f.url);
    const handleKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      else if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + fotoList.length) % fotoList.length);
      else if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % fotoList.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, asta]);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" /></div>;
  if (notFound || !asta) {
    return (
      <div className="text-center py-24 text-zinc-500" data-testid="asta-dettaglio-notfound">
        Annuncio non trovato.
        <div className="mt-4">
          <button onClick={() => navigate("/finder-aste")} className="text-[#EA7B2B] hover:text-[#F59E4A] text-sm">Torna a Finder Aste</button>
        </div>
      </div>
    );
  }

  const a = asta;
  const dispInfo = DISP[(a.disponibilita || [])[0]];
  const esitoInfo = ESITO[a.esito];
  const beneLabel = catBeneLabel((a.categoria_bene || [])[0]);
  const lottoLabel = a.numero_lotto === "LOTTO UNICO" ? "Lotto Unico" : (a.numero_lotto ? `Lotto ${a.numero_lotto}` : "—");
  const superficieNum = a.superficie ? parseFloat(String(a.superficie).replace(",", ".")) : null;
  const eurMq = superficieNum && superficieNum > 0 && a.offerta_minima != null ? a.offerta_minima / superficieNum : null;
  const storico = a.storico || { round: [], tentativi: 1, volte_deserta: 0, sconto_da_primo_round_pct: null };
  const stInfo = statoInfo(a.stato);
  const beni = a.beni && a.beni.length ? a.beni : null;
  // Le foto estratte da PDF (nessun URL nativo su PVP) hanno un url relativo /aste/... da
  // servire dal nostro backend; le foto native PVP hanno già un url assoluto.
  const foto = (a.foto || []).filter((f) => f.url).map((f) => ({ ...f, url: f.url.startsWith("/") ? `${API}${f.url}` : f.url }));

  const shareLink = `${window.location.origin}/finder-aste/${a.id_annuncio}`;
  const shareText = `${[a.citta, a.provincia && a.provincia !== a.citta ? `(${a.provincia})` : null].filter(Boolean).join(" ")} — ${a.indirizzo || ""} · Offerta minima ${eur(a.offerta_minima, 2)}`;
  const shareUrls = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareLink}`)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(shareText)}`,
    email: `mailto:?subject=${encodeURIComponent(`Asta immobiliare: ${a.citta || ""}`)}&body=${encodeURIComponent(`${shareText}\n\n${shareLink}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareLink)}`,
  };
  const shareNative = async () => {
    try {
      await navigator.share({ title: "Asta immobiliare", text: shareText, url: shareLink });
    } catch { /* utente ha annullato la condivisione */ }
    setShareOpen(false);
  };
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    }
    setShareOpen(false);
  };

  return (
    <div className="space-y-6" data-testid="asta-dettaglio-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <button onClick={() => navigate("/finder-aste")} data-testid="back-btn" className="mt-1 w-9 h-9 flex items-center justify-center border border-white/10 rounded-sm text-zinc-400 hover:bg-white/5 shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-light text-white break-words">
                {a.citta}{a.provincia && a.provincia !== a.citta ? ` (${a.provincia})` : ""}
              </h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border text-[#EA7B2B] border-[#EA7B2B]/30 bg-[#EA7B2B]/10">{catLottoLabel(a.categoria_lotto)}</span>
              {dispInfo && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border" style={{ color: dispInfo.color, borderColor: `${dispInfo.color}4d`, backgroundColor: `${dispInfo.color}1a` }}>{dispInfo.label}</span>}
              {esitoInfo && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border" style={{ color: esitoInfo.color, borderColor: `${esitoInfo.color}4d`, backgroundColor: `${esitoInfo.color}1a` }}>{esitoInfo.label}</span>}
              {stInfo && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border" style={{ color: stInfo.color, borderColor: `${stInfo.color}4d`, backgroundColor: `${stInfo.color}1a` }}>{stInfo.label}</span>}
            </div>
            <p className="text-sm text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
              <MapPin className="w-3.5 h-3.5 shrink-0" /> {a.indirizzo} · {a.tribunale} · RGE {a.procedura}{a.anno_rg ? `/${a.anno_rg}` : ""} - {lottoLabel}
            </p>
            {(a.tipo_vendita?.label || a.modalita_vendita?.label || a.tipo_procedura) && (
              <p className="text-xs text-zinc-500 mt-1">
                {a.tipo_procedura && <span>{a.tipo_procedura}</span>}
                {a.tipo_vendita?.label && <span> · Vendita {a.tipo_vendita.label}</span>}
                {a.modalita_vendita?.label && <span> · {a.modalita_vendita.label}</span>}
              </p>
            )}
            {beneLabel && <p className="text-xs text-zinc-600 mt-0.5 capitalize">{beneLabel}</p>}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <a href={a.url_annuncio} target="_blank" rel="noopener noreferrer" data-testid="asta-dett-link-annuncio" className="inline-flex items-center gap-1.5 text-xs text-[#EA7B2B] hover:text-[#F59E4A] border border-[#EA7B2B]/30 bg-[#EA7B2B]/5 px-3 py-1.5 rounded-sm transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Apri annuncio su PVP
              </a>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={a.stato || ""}
            onChange={(e) => changeStato(e.target.value)}
            disabled={savingStato}
            data-testid="asta-dett-stato-select"
            className="bg-[#122A3F] border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B] disabled:opacity-40"
          >
            <option value="">Nessuna classificazione</option>
            {STATO_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button
            onClick={togglePreferito}
            disabled={togglingPreferito}
            data-testid="asta-dett-preferito-btn"
            title={a.preferito ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
            className={`flex items-center gap-2 border text-sm font-medium px-3.5 py-2.5 rounded-sm transition-colors disabled:opacity-40 ${a.preferito ? "bg-[#EA7B2B] text-black border-[#EA7B2B]" : "bg-[#122A3F] border-white/15 text-white hover:border-[#EA7B2B]/60"}`}
          >
            <Star className={`w-4 h-4 ${a.preferito ? "fill-black" : ""}`} /> {a.preferito ? "Nei preferiti" : "Preferiti"}
          </button>
          <div className="relative">
            <button
              onClick={() => setShareOpen((o) => !o)}
              data-testid="asta-dett-share-btn"
              title="Condividi"
              className="flex items-center gap-2 bg-[#122A3F] border border-white/15 text-white text-sm font-medium px-3.5 py-2.5 rounded-sm hover:border-[#EA7B2B]/60 transition-colors"
            >
              <Share2 className="w-4 h-4" /> Condividi
            </button>
            {shareOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShareOpen(false)} />
                <div data-testid="asta-dett-share-menu" className="absolute right-0 mt-1 z-20 bg-[#122A3F] border border-white/15 rounded-sm shadow-xl py-1.5 w-56">
                  {typeof navigator !== "undefined" && navigator.share && (
                    <button onClick={shareNative} data-testid="asta-dett-share-native" className="flex items-center gap-2.5 w-full text-left px-3.5 py-2 text-sm text-zinc-200 hover:bg-white/5 transition-colors">
                      <Share2 className="w-4 h-4 text-[#EA7B2B]" /> Condividi…
                    </button>
                  )}
                  <a href={shareUrls.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="asta-dett-share-whatsapp" className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-white/5 transition-colors">
                    <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
                  </a>
                  <a href={shareUrls.telegram} target="_blank" rel="noopener noreferrer" data-testid="asta-dett-share-telegram" className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-white/5 transition-colors">
                    <Send className="w-4 h-4 text-[#229ED9]" /> Telegram
                  </a>
                  <a href={shareUrls.email} data-testid="asta-dett-share-email" className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-white/5 transition-colors">
                    <Mail className="w-4 h-4 text-zinc-400" /> Email
                  </a>
                  <a href={shareUrls.facebook} target="_blank" rel="noopener noreferrer" data-testid="asta-dett-share-facebook" className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-white/5 transition-colors">
                    <Facebook className="w-4 h-4 text-[#1877F2]" /> Facebook
                  </a>
                  <a href={shareUrls.x} target="_blank" rel="noopener noreferrer" data-testid="asta-dett-share-x" className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-white/5 transition-colors">
                    <Twitter className="w-4 h-4 text-zinc-400" /> X (Twitter)
                  </a>
                  <button onClick={copyShareLink} data-testid="asta-dett-share-copy" className="flex items-center gap-2.5 w-full text-left px-3.5 py-2 text-sm text-zinc-200 hover:bg-white/5 transition-colors border-t border-white/10 mt-1 pt-2.5">
                    <LinkIcon className="w-4 h-4 text-zinc-400" /> Copia link
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Countdown */}
        <Section title="Tempo rimanente per offerte" icon={Clock} testid="asta-dett-countdown-section">
          <Countdown scadenza={a.scadenza_offerte} />
        </Section>

        {/* Prezzi */}
        <Section title="Prezzi" icon={Gavel} testid="asta-dett-prezzi-section">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            <span className="text-zinc-500">Prezzo base</span><span className="text-right font-mono text-zinc-200">{eur(a.prezzo_base, 2)}</span>
            <span className="text-zinc-500">Offerta minima</span><span className="text-right font-mono text-white">{eur(a.offerta_minima, 2)}</span>
            {a.rialzo_minimo != null && <><span className="text-zinc-500">Rialzo minimo</span><span className="text-right font-mono text-zinc-400">{eur(a.rialzo_minimo, 2)}</span></>}
            {a.valore_stimato != null && <><span className="text-zinc-500">Valore stimato (perizia)</span><span className="text-right font-mono text-zinc-200">{eur(a.valore_stimato, 2)}</span></>}
            {eurMq != null && <><span className="text-zinc-500">€/mq (su offerta minima)</span><span className="text-right font-mono text-[#EA7B2B]">{eur(eurMq, 0)}</span></>}
          </div>
          {/* Testo libero inserito dal tribunale: percentuale, importo assoluto o frase
              intera ("10%", "1610", "almeno il 20% del prezzo offerto") a seconda di come
              lo ha compilato il singolo tribunale — mostrato così com'è, senza forzare un
              formato valuta che per molti annunci sarebbe sbagliato. */}
          {(a.deposito_cauzionale || a.deposito_fondo_spese) && (
            <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5 text-xs">
              {a.deposito_cauzionale && <div className="text-zinc-400"><span className="text-zinc-600">Deposito cauzionale: </span>{a.deposito_cauzionale}</div>}
              {a.deposito_fondo_spese && <div className="text-zinc-400"><span className="text-zinc-600">Fondo spese: </span>{a.deposito_fondo_spese}</div>}
            </div>
          )}
        </Section>

        {/* Storico */}
        <Section title="Storico asta" icon={TrendingDown} testid="asta-dett-storico-section">
          {storico.tentativi <= 1 ? (
            <div className="text-sm text-emerald-400">Asta nuova — nessun tentativo precedente.</div>
          ) : (
            <div className="space-y-2.5">
              <div className="text-sm">
                <span className="text-rose-400 font-mono font-medium">{storico.volte_deserta}</span> <span className="text-zinc-500">{storico.volte_deserta === 1 ? "asta andata deserta" : "aste andate deserte"}</span>
                {storico.sconto_da_primo_round_pct != null && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-emerald-400 border-emerald-400/40 bg-emerald-400/10">
                    −{storico.sconto_da_primo_round_pct}% dal primo round
                  </span>
                )}
              </div>
              <div className="space-y-1 text-xs">
                {storico.round.map((r) => (
                  <div key={r.id_annuncio} className={`flex items-center justify-between px-2 py-1 rounded-sm ${r.id_annuncio === a.id_annuncio ? "bg-[#EA7B2B]/10 text-white" : "text-zinc-400"}`}>
                    <span className="font-mono">{r.data_vendita ? new Date(r.data_vendita).toLocaleDateString("it-IT") : "—"}</span>
                    <span className="font-mono">{eur(r.prezzo_base, 0)}</span>
                    <span className={ESITO[r.esito] ? "" : "text-zinc-600"} style={ESITO[r.esito] ? { color: ESITO[r.esito].color } : {}}>
                      {r.id_annuncio === a.id_annuncio ? "questo annuncio" : (ESITO[r.esito]?.label || "—")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Immobile/i */}
      <Section
        title={beni && beni.length > 1 ? `Immobili del lotto (${beni.length})` : "Immobile"}
        icon={Ruler}
        testid="asta-dett-immobile-section"
        action={a.diritto_proprieta && a.diritto_proprieta !== "-" && (
          <span
            data-testid="asta-dett-diritto"
            title={a.diritto_proprieta === "?" ? "Nella descrizione compaiono più diritti diversi: verificare l'annuncio" : "Estratto automaticamente dalla descrizione del lotto"}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border whitespace-nowrap ${a.diritto_proprieta === "?" ? "text-amber-400 border-amber-400/40 bg-amber-400/10" : "text-[#EA7B2B] border-[#EA7B2B]/30 bg-[#EA7B2B]/10"}`}
          >
            Diritto: {a.diritto_proprieta}
          </span>
        )}
      >
        {!beni ? (
          <div className="text-sm text-zinc-500">Dati immobile non disponibili per questo annuncio.</div>
        ) : (
          <div className="space-y-5">
            {beni.map((b, i) => (
              <div key={i} className={i > 0 ? "pt-5 border-t border-white/10" : ""}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm mb-2">
                  {b.tipologia && <span className="text-white font-medium">{b.tipologia}</span>}
                  <span className="text-zinc-400">Superficie: <span className="font-mono text-zinc-200">{b.superficie ? `${b.superficie} mq` : "n.d."}</span></span>
                  <span className="text-zinc-400">Vani: <span className="font-mono text-zinc-200">{b.numero_vani ?? "n.d."}</span></span>
                  <span className="text-zinc-400">Piano: <span className="font-mono text-zinc-200 capitalize">{b.piano || "n.d."}</span></span>
                  {b.disponibilita && <span className="text-zinc-400">Stato: <span className="text-zinc-200">{b.disponibilita}</span></span>}
                </div>
                {(b.indirizzo?.via || b.indirizzo?.comune) && (
                  <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> {[b.indirizzo.via, b.indirizzo.comune, b.indirizzo.provincia, b.indirizzo.regione].filter(Boolean).join(", ")}
                  </div>
                )}
                {(b.dati_catastali || []).length > 0 && (
                  <div className="text-xs text-zinc-400 mb-2 flex items-center gap-2 flex-wrap">
                    <span>Catasto: {b.dati_catastali.map((c, ci) => (
                      <span key={ci} className="font-mono text-zinc-300">
                        {[c.textFoglio && `F.${c.textFoglio}`, c.textParticella && `P.${c.textParticella}`, c.textSubalterno && `Sub.${c.textSubalterno}`].filter(Boolean).join(" ")}
                        {ci < b.dati_catastali.length - 1 ? " · " : ""}
                      </span>
                    ))}</span>
                    {b.dati_catastali[0]?.fonte === "perizia" && (
                      <span title="PVP non fornisce questo dato in modo strutturato: estratto automaticamente dal testo della perizia, verificare prima di usarlo" className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-amber-400 border-amber-400/40 bg-amber-400/10">
                        Da perizia · da verificare
                      </span>
                    )}
                  </div>
                )}
                {b.descrizione && <p className="text-xs text-zinc-500 leading-relaxed mb-2 whitespace-pre-line">{b.descrizione}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Foto: unite da allegati di lotto e di ogni bene (deduplicate lato backend), perché
          a seconda dell'annuncio le foto possono stare solo nell'uno o solo nell'altro. */}
      {foto.length > 0 && (
        <Section title={`Foto (${foto.length})`} icon={ImageIcon} testid="asta-dett-foto-section">
          <div className="flex flex-wrap gap-2">
            {foto.map((f, i) => (
              <button key={f.nome || i} onClick={() => setLightboxIndex(i)} data-testid={`asta-dett-foto-thumb-${i}`} title={f.nome} className="block w-24 h-24 rounded-sm overflow-hidden border border-white/10 hover:border-[#EA7B2B]/60 transition-colors shrink-0">
                <img src={f.url} alt={f.nome || "foto immobile"} loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </Section>
      )}

      {lightboxIndex != null && foto[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          data-testid="asta-dett-lightbox"
          onClick={() => setLightboxIndex(null)}
        >
          <button onClick={() => setLightboxIndex(null)} data-testid="asta-dett-lightbox-close" className="absolute top-4 right-4 text-white/70 hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
          {foto.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + foto.length) % foto.length); }}
              data-testid="asta-dett-lightbox-prev"
              className="absolute left-2 sm:left-6 text-white/70 hover:text-white p-2"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          <img
            src={foto[lightboxIndex].url}
            alt={foto[lightboxIndex].nome || "foto immobile"}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[85vh] object-contain rounded-sm"
          />
          {foto.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % foto.length); }}
              data-testid="asta-dett-lightbox-next"
              className="absolute right-2 sm:right-6 text-white/70 hover:text-white p-2"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/60 font-mono">
            {lightboxIndex + 1} / {foto.length}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Referenti */}
        <Section title="Referenti" icon={Users} testid="asta-dett-referenti-section">
          {!(a.soggetti || []).length ? (
            <div className="text-sm text-zinc-500">Nessun referente disponibile.</div>
          ) : (
            <div className="space-y-3">
              {a.soggetti.map((s, i) => (
                <div key={i} className="flex items-start justify-between gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0" data-testid={`asta-dett-soggetto-${i}`}>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#EA7B2B]">{RUOLO_LABELS[s.ruolo] || s.ruolo}</div>
                    <div className="text-sm text-white">{[s.nome, s.cognome].filter(Boolean).join(" ")}</div>
                  </div>
                  <div className="text-right text-xs space-y-1">
                    {s.telefono && <a href={`tel:${s.telefono}`} className="flex items-center justify-end gap-1.5 text-zinc-400 hover:text-[#EA7B2B]"><Phone className="w-3 h-3" /> {s.telefono}</a>}
                    {s.email && <a href={`mailto:${s.email}`} className="flex items-center justify-end gap-1.5 text-zinc-400 hover:text-[#EA7B2B] break-all"><Mail className="w-3 h-3 shrink-0" /> {s.email}</a>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Documenti */}
        <Section title="Documenti" icon={FileText} testid="asta-dett-documenti-section">
          {!(a.documenti || []).length ? (
            <div className="text-sm text-zinc-500">Nessun documento elencato.</div>
          ) : (
            <>
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {a.documenti.map((d, i) => {
                  const row = (
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-white/15 text-zinc-400 shrink-0">{DOC_TYPE_LABELS[d.tipo] || d.tipo || "—"}</span>
                      <span className="truncate" title={d.nome}>{d.nome}</span>
                    </span>
                  );
                  return d.url ? (
                    <a key={i} href={d.url} target="_blank" rel="noopener noreferrer" data-testid={`asta-dett-doc-${i}`} className="flex items-center justify-between gap-2 text-xs text-zinc-300 hover:text-[#EA7B2B] transition-colors">
                      {row}
                      <span className="flex items-center gap-1.5 text-zinc-600 shrink-0"><Download className="w-3 h-3" /> {formatBytes(d.dimensione)}</span>
                    </a>
                  ) : (
                    <div key={i} data-testid={`asta-dett-doc-${i}`} className="flex items-center justify-between gap-2 text-xs">
                      {row}
                      <span className="text-zinc-600 shrink-0">{formatBytes(d.dimensione)}</span>
                    </div>
                  );
                })}
              </div>
              <a href={a.url_annuncio} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-[#EA7B2B] hover:text-[#F59E4A]">
                <ExternalLink className="w-3.5 h-3.5" /> Apri annuncio su PVP
              </a>
            </>
          )}
        </Section>
      </div>

      {/* Mappa / Street View */}
      <Section title="Posizione" icon={MapPin} testid="asta-dett-mappa-section"
        action={
          <div className="flex items-center gap-3">
            {a.lat != null && a.lng != null && GOOGLE_MAPS_KEY && (
              <div className="flex items-center bg-[#0A1826] border border-white/15 rounded-sm p-0.5">
                <button onClick={() => setMapMode("streetview")} data-testid="asta-dett-map-mode-streetview" className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${mapMode === "streetview" ? "bg-[#EA7B2B] text-black" : "text-zinc-400 hover:text-white"}`}>
                  Street View
                </button>
                <button onClick={() => setMapMode("maps")} data-testid="asta-dett-map-mode-maps" className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${mapMode === "maps" ? "bg-[#EA7B2B] text-black" : "text-zinc-400 hover:text-white"}`}>
                  Mappa
                </button>
              </div>
            )}
            {a.lat != null && a.lng != null && (
              <a href={mapsUrl(a.lat, a.lng)} target="_blank" rel="noopener noreferrer" data-testid="asta-dett-maps-btn" className="inline-flex items-center gap-1.5 text-xs text-[#EA7B2B] hover:text-[#F59E4A]">
                <ExternalLink className="w-3.5 h-3.5" /> Apri in Google Maps
              </a>
            )}
          </div>
        }>
        {a.lat == null || a.lng == null ? (
          <div className="text-sm text-zinc-500">Coordinate non disponibili per questo annuncio.</div>
        ) : !GOOGLE_MAPS_KEY ? (
          <div className="text-sm text-zinc-500">Street View non configurato.</div>
        ) : (
          <iframe
            title="street-view"
            data-testid="asta-dett-streetview"
            width="100%"
            height="360"
            style={{ border: 0, borderRadius: 4 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={mapMode === "maps"
              ? `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_KEY}&q=${a.lat},${a.lng}&zoom=17`
              : `https://www.google.com/maps/embed/v1/streetview?key=${GOOGLE_MAPS_KEY}&location=${a.lat},${a.lng}&fov=90`}
          />
        )}
      </Section>

      {/* Nota privata */}
      <Section title="Nota privata" icon={FileText} testid="asta-dett-nota-section">
        <textarea
          value={notaText}
          onChange={(e) => setNotaText(e.target.value)}
          placeholder="Annotazioni private legate al tuo account, visibili solo a te…"
          rows={4}
          data-testid="asta-dett-nota-textarea"
          className="w-full bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B] resize-none"
        />
        <button onClick={saveNota} disabled={savingNota} data-testid="asta-dett-nota-save" className="mt-2 flex items-center gap-2 bg-[#EA7B2B] text-black text-sm font-medium px-3.5 py-2 rounded-sm hover:bg-[#F59E4A] transition-colors disabled:opacity-40">
          {savingNota ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva nota
        </button>
      </Section>

      {/* Prossimamente */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {["Valutazione immobile (PVM)", "Visura catastale", "Visura ipotecaria"].map((label) => (
          <Section key={label} title={label} icon={Lock} locked testid={`asta-dett-locked-${label}`}>
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Lock className="w-4 h-4" /> Prossimamente — acquisto visure in arrivo.
            </div>
          </Section>
        ))}
      </div>
    </div>
  );
}
